import * as React from 'react';
import { ReactNode, useContext, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { ReferencedWorkDTO, WorkApi, WorkDTO } from '../../api/core';
import { IntlShape, useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import {
  referencedWorkToSelectedWork,
  SelectedWork,
  selectedWorkToReferencedWork,
  WorkSuggester,
} from '../edit/components/suggester/WorkSuggester';
import { ParsedMathJax } from './ParsedMathJax';
import { AuthContext } from '../../Auth/components/AuthProvider';
import { getAuthMiddleware } from '../../Auth/utils/GetAuthMiddleware';

interface Props {
  id: number;
  title: string;
  visible: boolean;
  setVisible: (visible: boolean) => void;
  onSuccess: (work: WorkDTO) => void;
  onError: (error: unknown) => void;
}

export const DuplicateDialog = (props: Props): ReactNode => {
  const intl: IntlShape = useIntl();
  const [duplicates, setDuplicates] = useState<ReferencedWorkDTO[]>([]);
  const {actions} = useContext(AuthContext);
  const workApi: WorkApi = new WorkApi().withMiddleware(getAuthMiddleware(actions));
  const [merging, setMerging] = useState<boolean>(false);

  const close = (): void => {
    setDuplicates([]);
    props.setVisible(false);
  };

  const merge = async (): Promise<void> => {
    setMerging(true);
    const requestBody: number[] = duplicates.map((duplicate: ReferencedWorkDTO): number => duplicate.id);
    await workApi
      .mergeDuplicates({
        unicumId: props.id,
        requestBody
      })
      .then((work: WorkDTO): void => {
        close();
        props.onSuccess(work);
      })
      .catch((e): void => props.onError(e))
      .finally((): void => setMerging(false));
  };

  return <Dialog header={intl.formatMessage({id: 'work.button.setAsOriginal'})}
                 visible={props.visible}
                 style={{width: '100%', maxWidth: '1000px'}}
                 closeOnEscape={true}
                 dismissableMask={true}
                 onHide={close}>
    <div className={'m-0 flex flex-column gap-4'}>
      <p className={'flex gap-1'}><ParsedMathJax content={props.title} /> (ID: {props.id})</p>
      <h4>{intl.formatMessage({id: 'work.duplicatePopup.setAsOriginal'})}</h4>
      <WorkSuggester
        value={duplicates.map(referencedWorkToSelectedWork)}
        inputId={'suggester-duplicate-works'}
        required={false}
        disabled={merging}
        validation={{valid: true}}
        onChange={(refs: SelectedWork[]): void => setDuplicates(refs.map(selectedWorkToReferencedWork))}
        multiple={true}
      />
      <div className={'flex gap-4 justify-content-end'}>
        <Button severity={'secondary'}
                label={intl.formatMessage({id: 'button.cancel'})}
                disabled={merging}
                onClick={close}/>
        <Button onClick={merge}
                disabled={duplicates.length === 0 || merging}
                label={intl.formatMessage({id: 'work.duplicatePopup.button.setAsOriginal'})}/>
      </div>
    </div>
  </Dialog>;
};
